/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { screens, Theme, spacingLevel } from '../design-system'
import { FranklinGothic, NeueHaas } from '../assets/fonts'
import { LayoutCard, FeatureCard, Spotlight } from '../twt-core'
import { FeatureCard2020 } from '../twt-2020/organisms/feature-card.2020'
import { SectionWrapper, SectionTitle } from '../twt-core/molecules/section'
import {
  SectionWrapper2020,
  SectionTitle2020,
} from '../twt-2020/molecules/section.2020'
import { ArrowLink, ArrowButton } from '../twt-core/molecules/arrow-link'
import {
  ArrowLink2020,
  ArrowButton2020,
} from '../twt-2020/molecules/arrow-link.2020'
import { PageTitle } from '../twt-core/organisms/page-title'
import { PageTitle2021 } from './organisms/page-title.2021'
import { Spotlight2020 } from '../twt-2020/organisms/spotlight.2020'
import { DocumentDownloadBlockElement2020 } from '../twt-2020/molecules/blocks.2020'
import { DigitalStalls2020 } from '../twt-2020/organisms/digital-stalls-subpage-list.2020'
import { SubpageGrid } from '../twt-core/organisms/subpage-grid'
import { DocumentDownloadBlockElement } from '../cms/universal-blocks'

export const colors = {
  // TWT21 Design Colours
  navy: '#1E008E',
  green: '#00FF80',
  greenTranslucent: 'rgba(0, 255, 128, 0.6)',
  pink: '#FFC1FF',
  yellow: '#FFE4A9',
  orange: '#FFC591',
  blue: '#95CCE3',
  darkPink: '#FF5E80',
  purple: '#877ACA',

  // Legacy Colours
  black: '#000000',
  tomato: '#FF604C',
  skyBlue: '#CCFEF4',
  lilac: '#DBCEFF',
  cyan: '#00FBC7',
  twtGreen: '#00FAC8',
  indigo: '#784BFB',
  magenta: '#FF2C63',
  darkGrey: '#8C8C8C',
  grey: '#DDDDDD',
  lightGrey: '#F5F5F5',
  blue: '#1E008E',
}

export const themeColors = {
  ...colors,
  bg: 'white',
  foreground: 'white',
  bgAlt: colors.indigo,
  content: colors.blue,
  contentLight: colors.blue,
  contentAlt: colors.green,
  link: colors.indigo,
  linkActive: colors.tomato,
  control: colors.black,
}

export const themeFonts = {
  regular: css`
    color: ${themeColors.navy};
    font-family: 'AA Gothic Bold', SpaceMono, sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 140%;
    letter-spacing: -0.03em;

    a {
      text-decoration: underline;
    }
  `,
  small: css`
    color: ${themeColors.navy};
    font-family: 'AA Gothic Bold', SpaceMono, sans-serif;
    font-weight: 400;
    font-size: 18px;
    line-height: 150%;
  `,
  control: css`
    color: ${themeColors.navy};
    font-family: 'AA Gothic Bold', SpaceMono, sans-serif;
    font-weight: 500;
    font-size: 18px;
    line-height: 150%;
  `,
  controlSection: css`
    color: ${themeColors.navy};
    font-family: 'AA Gothic Bold', SpaceMono, sans-serif;
    font-weight: 500;
    font-size: 14.6029px;
    line-height: 100%;
    display: flex;
    align-items: center;
    text-transform: uppercase;
  `,
  heading: css`
    color: ${themeColors.navy};
    font-family: NeueHaas;
    font-weight: bold;

    font-size: 22px;

    @media (min-width: ${screens.desktop}px) {
      font-size: 22px;
    }
  `,
  eventCardHeading: css`
    font-family: 'AA Gothic Bold', SpaceMono, sans-serif;
    color: ${themeColors.navy};
    font-weight: bold;
    font-size: 30px;
    @media screen and (max-width: 768px) {
      font-size: 25px;
    }
  `,
  filterButton: css`
    font-family: 'AA Gothic Bold', SpaceMono, sans-serif;
    color: ${themeColors.navy};
    font-weight: bold;

    font-size: 22px;
  `,
  headingSmall: css`
    font-family: FranklinGothic;
    font-weight: 300;
    font-size: 24px;
    line-height: 36px;
    letter-spacing: -0.03em;
  `,
  display: css`
    color: ${themeColors.navy};
    margin: 0;
    font-family: FranklinGothic;
    font-style: normal;
    font-weight: bold;
    font-size: 51px;
    line-height: 82.8%;

    @media (min-width: ${screens.tablet}px) {
      font-size: 87px;
      line-height: 84%;
    }
  `,
  link: css`
    .active,
    :active {
      opacity: 0.5;
    }

    &:hover:not(:active):not(.active) {
      opacity: 0.5;
    }
  `,
}

export const fonts2021 = [NeueHaas, FranklinGothic]

const getTheme2021 = (overrides: Partial<typeof themeColors> = {}) =>
  new Theme({ ...themeColors, ...overrides }, themeFonts)
    .override(LayoutCard, (props) => (
      <LayoutCard.Original
        gutter={false}
        padding={{ mobile: 2, desktop: 3 }}
        {...props}
      />
    ))
    .override(FeatureCard, FeatureCard2020)
    .override(SectionWrapper, SectionWrapper2020)
    .override(ArrowLink, ArrowLink2020)
    .override(ArrowButton, ArrowButton2020)
    .override(PageTitle, PageTitle2021)
    .override(SectionTitle, SectionTitle2020)
    .override(Spotlight, Spotlight2020)
    .override(SubpageGrid, DigitalStalls2020)
    .override(DocumentDownloadBlockElement, DocumentDownloadBlockElement2020)
    .style('button', {
      default: ({ bg, color }) => ({
        border: '2px solid ' + (color || 'black'),
        color: color || 'inherit',
        fontSize: 34,
        backgroundColor: bg || 'rgba(0,0,0,0)',
      }),
      solid: ({ bg }) => ({
        border: 'none',
        color: colors.navy,
        paddingRight: spacingLevel(3),
        paddingLeft: spacingLevel(3),
      }),
      chip: {
        borderRadius: 1.73844,
        fontWeight: 500,
        fontSize: 14.6029,
        border: `1.73844px solid ${colors.indigo}`,
      },
      filter: {
        padding: `${spacingLevel(1)}px ${spacingLevel(3)}px`,
        border: `2px solid ${colors.indigo}`,
        borderRadius: 4,
        color: colors.indigo,
        backgroundColor: 'rgba(0,0,0,0)',
        fontWeight: 500,
        fontSize: 18,
        lineHeight: '100%',
        textTransform: 'uppercase',
        '&:hover': {
          opacity: 0.6,
          backgroundColor: 'rgba(0,0,0,0)',
        },
      },
      toggleChip: {
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: colors.greenTranslucent,
        },
        padding: `22px 28px`,
        border: `5px solid ${colors.navy}`,
        color: colors.navy,
        backgroundColor: 'rgba(0,0,0,0)',
        borderRadius: 9000,
        fontWeight: 700,
        fontSize: 26,
        lineHeight: '100%',
        marginRight: 12,
        '&:last-of-type': {
          marginRight: 0,
        },
      },
      toggleChipActive: {
        padding: `22px 28px`,
        border: `5px solid ${colors.navy}`,
        color: colors.navy,
        backgroundColor: colors.green,
        borderRadius: 9000,
        fontWeight: 700,
        fontSize: 26,
        lineHeight: '100%',
        marginRight: 12,
        '&:last-of-type': {
          marginRight: 0,
        },
      },
      squareChip: {
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: colors.greenTranslucent,
        },
        padding: `12px 10px`,
        border: `4px solid ${colors.navy}`,
        color: colors.navy,
        backgroundColor: 'rgba(0,0,0,0)',
        fontWeight: 700,
        fontSize: 20,
        lineHeight: '100%',
      },
      squareChipActive: {
        padding: `12px 10px`,
        border: `4px solid ${colors.navy}`,
        color: colors.navy,
        backgroundColor: colors.green,
        fontWeight: 700,
        fontSize: 20,
        lineHeight: '100%',
      },
    })
    .style('menuDivider', {
      default: `
      margin-left: 1.25em;
      ::before {
        content: none !important;
      }
    `,
    })
    .style('card', {
      event: ({ borderColor }) => ({
        borderLeft: `50px solid ${themeColors[borderColor] || colors.yellow}`,
        backgroundColor: '#FFFFFF',
        '&:hover': {
          opacity: '0.9 !important',
        },
      }),
      stall: `
      color: ${colors.indigo};
      border: 2px solid ${colors.indigo};
      box-shadow: 3px 3px 4px rgba(0, 0, 0, 0.2);
      background: #FFFFFF;
      box-shadow: 3px 3px 4px rgba(0, 0, 0, 0.2);
      border-radius: 4px;
    `,
    })

export const theme2021 = getTheme2021()
